import { useEffect, type ReactNode } from 'react'

export function Avatar({
  emoji,
  color,
  size = 'md',
  ring = false,
}: {
  emoji: string
  color?: string
  size?: 'sm' | 'md' | 'lg'
  ring?: boolean
}) {
  const className = ['avatar', size === 'lg' ? 'avatar-lg' : size === 'sm' ? 'avatar-sm' : ''].join(' ')
  return (
    <span
      className={className}
      style={ring && color ? { borderColor: color, background: `${color}22` } : undefined}
      aria-hidden="true"
    >
      {emoji}
    </span>
  )
}

export function Meter({
  value,
  max,
  tone = 'accent',
  label,
}: {
  value: number
  max: number
  tone?: 'accent' | 'ok' | 'warn' | 'danger'
  label?: string
}) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100))
  const fillTone = tone === 'accent' ? '' : `meter-fill-${tone}`
  return (
    <div
      className="meter"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <div className={`meter-fill ${fillTone}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export function Pill({
  children,
  tone = 'default',
}: {
  children: ReactNode
  tone?: 'default' | 'ok' | 'warn' | 'danger' | 'accent'
}) {
  const cls = tone === 'default' ? 'pill' : `pill pill-${tone}`
  return <span className={cls}>{children}</span>
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="empty">
      <div style={{ fontWeight: 600 }}>{title}</div>
      {hint ? <div className="faint">{hint}</div> : null}
    </div>
  )
}

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Loading" />
}

export function Loading() {
  return (
    <div className="page" style={{ alignItems: 'center', paddingTop: 'var(--space-7)' }}>
      <Spinner />
    </div>
  )
}

export function Check({ done }: { done: boolean }) {
  return (
    <span className="check-box">
      {done ? (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path
            d="M2 7.5 5.5 11 12 3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  )
}

export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grip" />
        <div className="row-between">
          <h2 className="title">{title}</h2>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : 'Something went wrong.'
  return <p className="error-text">{message}</p>
}

import { MIN_PASSWORD_LENGTH } from '../../shared/auth'

export { MIN_PASSWORD_LENGTH }

/**
 * Says why a password submit is still disabled. A greyed-out button with only a
 * faint hint reads as broken, so once typing starts this counts what's missing.
 */
export function PasswordHint({ password }: { password: string }) {
  const remaining = MIN_PASSWORD_LENGTH - password.length
  if (password.length === 0 || remaining <= 0) {
    return <span className="faint">At least {MIN_PASSWORD_LENGTH} characters.</span>
  }
  return (
    <span className="error-text">
      {remaining} more {remaining === 1 ? 'character' : 'characters'} — at least {MIN_PASSWORD_LENGTH}.
    </span>
  )
}
